import type { ReactNode } from 'react';
import { cn } from '@immo/ui-kit';
import { ColumnGrip } from './column-grip';
import { ColumnHeaderMenu } from './column-header-menu';
import type { ColumnHeadControls } from './use-configurable-columns';

type ColumnHeadContentProps = {
  readonly controls: ColumnHeadControls;
  /** null for a column the table cannot sort by: the menu's sort items are absent. */
  readonly onSort: ((direction: 'asc' | 'desc') => void) | null;
  /** The table's own label or sort button — the kit does not own a table's sort widget. */
  readonly children: ReactNode;
};

export function ColumnHeadContent({ controls, onSort, children }: ColumnHeadContentProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-1',
        // Inside the cell: under `border-collapse` a table cell ignores `border-radius`.
        controls.dragged && 'rounded-md bg-surface shadow-md',
      )}
    >
      {controls.draggable && (
        // The only draggable element in the cell, so a press elsewhere still sorts.
        <ColumnGrip
          columnLabel={controls.columnLabel}
          onDragStart={controls.onDragStart}
          onDragEnd={controls.onDragEnd}
          className="group-hover/col:opacity-100"
        />
      )}
      <div className="min-w-0 flex-1">{children}</div>
      <ColumnHeaderMenu
        className="shrink-0"
        columnLabel={controls.columnLabel}
        canMoveLeft={controls.canMoveLeft}
        canMoveRight={controls.canMoveRight}
        onMove={controls.onMove}
        onSort={onSort}
        onHide={controls.onHide}
        onOpenPanel={controls.onOpenPanel}
      />
    </div>
  );
}
